import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { orderBy } from '../../../core/helpers/array-utils';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { StaticDataActions } from '../../../state';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { voucherImage } from '../../../core/helpers';

@Component({
    selector: 'gs-gift-cards-list',
    templateUrl: './gift-cards-list.component.html',
    standalone: false
})
export class GiftCardsListComponent implements OnInit, OnDestroy {
  @Input() showFilters: boolean = true;
  @Input() filterOutCategories: number[];
  @Input() filterInCategories: number[];
  @Input() itemsPerPage: number = 24;
  @ViewChild('filtersTemplate') filtersTemplateRef: TemplateRef<any>;

  vouchers: any[];
  filteredVouchers: any[];
  categoryOptions: any = [];
  categories: any = [];
  locationOptions: any = [];
  locations: any = [];

  page: number = 1;
  sort: string = "implicit";
  activeFiltersCollapsed: boolean = false;

  form: FormGroup = this.formBuilder.group({
    // sort: ['implicit', [Validators.required]],
    categories: this.formBuilder.array([]),
    locations: this.formBuilder.array([]),
    keyword: [null]
  });

  private subscriptions: Subscription[] = [];

  get controls() {
    return this.form.controls;
  }

  get categoriesArray() {
    return this.form.controls['categories'] as FormArray
  }

  get locationsArray() {
    return this.form.controls['locations'] as FormArray
  }

  get keywordFilter() {
    return this.form.value.keyword;
  }

  get categoriesFilter() {
    return this.form.value.categories.map((checked, index) => checked ? this.categories[index] : null).filter(x => x != null);
  }

  get locationsFilter() {
    return this.form.value.locations.map((checked, index) => checked ? this.locations[index] : null).filter(x => x != null);
  }

  get hasFilters() {
    return this.keywordFilter || this.categoriesFilter?.length || this.locationsFilter?.length;
  }

  get hasPagination(): boolean {
    return this.filteredVouchers.length >= this.itemsPerPage;
  }

  get lastPage(): number {
    return Math.floor(this.filteredVouchers.length / this.itemsPerPage);
  }

  get isCharity(): boolean {
    return this.filterInCategories && this.filterInCategories.includes(environment.charityCategoryId);
  }

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private offcanvasService: NgbOffcanvas,
  ) { }

  ngOnInit() {
    this.store.dispatch([
      new StaticDataActions.GetCategories(),
      new StaticDataActions.GetLocations()
    ]);

    // this.subscriptions.push(
    //   this.store.select<UserProfile>(state => state.auth.user)
    //     .subscribe(user => {
    //       this.userId = user?.id;
    //     })
    // );

    this.getCategories();
    this.getLocations();
    this.getVouchers();

    this.subscriptions.push(
      this.form.valueChanges
        .pipe(
          debounceTime(100),
          distinctUntilChanged()
        )
        .subscribe(x => {
          this.filterVouchers(
            this.sort,
            !x.keyword || x.keyword.length < 3 ? '' : x.keyword,
            x.categories.map((checked, index) => checked ? this.categories[index].id : null).filter(x => x != null),
            x.locations.map((checked, index) => checked ? this.locations[index].id : null).filter(x => x != null)
          );
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  openFilterMenu() {
    this.offcanvasService.open(this.filtersTemplateRef, {panelClass: 'offcanvas-lg', position: 'start'});
  }

  private getLocations() {
    this.subscriptions.push(
      this.store.select<any>(state => state.staticData.locations)
        .pipe(filter(x => !!x))
        .subscribe((response: any) => {
          this.locations = response.map(x => ({ ...x })).sort((a, b) => a.name.localeCompare(b.name));
          // this.locationOptions = [
          //   {
          //     id: null,
          //     text: 'Toate orasele'
          //   },
          //   ...this.locations.map(x => ({
          //     id: x.id,
          //     text: x.name
          //   }))
          // ];
          this.locationsArray.clear();
          this.locations.forEach(() => {
            this.locationsArray.push(this.formBuilder.control(null, []));
          });
        })
    );
  }

  private getCategories() {
    this.subscriptions.push(
      this.store.select<any>(state => state.staticData.categories)
        .pipe(filter(x => !!x))
        .subscribe((response: any) => {
          this.categories = this.preFilterCategories(response.map(x => ({ ...x })).sort((a, b) => a.name.localeCompare(b.name)));
          // this.categoryOptions = [
          //   {
          //     id: null,
          //     text: 'Toate categoriile'
          //   },
          //   ...this.categories.map(x => ({
          //     id: x.id,
          //     text: x.name
          //   }))
          // ];

          this.categoriesArray.clear();
          this.categories.forEach(() => {
            this.categoriesArray.push(this.formBuilder.control(null, []));
          });
        })
    );
  }

  private getVouchers() {
    this.vouchers = null;
    this.subscriptions.push(
      this.store.select<any>(state => state.staticData.vouchers)
        .pipe(filter(x => !!x))
        .subscribe(response => {
          const list = this.preFilterVouchers(response.map(x => ({ ...x })));
          list.forEach((x) => {
            x.image = voucherImage(x.image);

            if (x.fixedRange) {
              const fixedRanges = x.fixedRange.split('|');
              fixedRanges.sort((a, b) => {
                if (parseFloat(a) < parseFloat(b)) {
                  return -1;
                } else if (parseFloat(a) > parseFloat(b)) {
                  return 1;
                } else {
                  return 0;
                }
              });

              x.rangeMinValue = fixedRanges[0];
              x.rangeMaxValue = fixedRanges[fixedRanges.length - 1];

              return fixedRanges;
            }
          });

          this.vouchers = list;
          this.filterVouchers();
        })
    );
  }

  private filterVouchers(sort: string = "implicit", keyword: string = null, categories: number[] = null, locations: number[] = null) {
    if (!this.vouchers) {
      return;
    }

    const list = this.vouchers.filter(x =>
      (!categories?.length || x.partnerCategories.some(y => categories.includes(y.id))) &&
      (!locations?.length || x.partnerLocations.some(y => locations.includes(y.id))) &&
      (keyword == null || keyword == '' || x.name?.toLowerCase().indexOf(keyword.toLowerCase()) >= 0 ||
                                           x.partnerName?.toLowerCase().indexOf(keyword.toLowerCase()) >= 0 ||
                                           x.partnerDescription?.toLowerCase().indexOf(keyword.toLowerCase()) >= 0
                                          )
    );
    this.filteredVouchers = this.sortVouchers(list, sort);
  }

  onSortChange() {
    this.sortVouchers(this.filteredVouchers, this.sort)
  }

  onRemoveFilterClick(filter: any) {
    this.form.patchValue({
      ...filter
    });
  }

  onRemoveAllFiltersClick() {
    this.form.reset({
      keyword: null,
      categories: [],
      locations: [],
    });
  }

  onRemoveCategoryFilterClick(categoryId: number) {
    const categoryIndex = this.categories.findIndex(x => x.id === categoryId);
    this.onRemoveFilterClick({
      categories: this.form.value.categories.map((checked, index) => index === categoryIndex ? false : checked)
    });
  }

  onRemoveLocationFilterClick(locationId: number) {
    const locationIndex = this.locations.findIndex(x => x.id === locationId);
    this.onRemoveFilterClick({
      locations:  this.form.value.locations.map((checked, index) => index === locationIndex ? false : checked)
    });
  }

  onScrolled() {
    if (this.page <= this.lastPage) {
      this.page++;
    }
  }

  /**
   * Filter in and out categories by categoryId
   *
   * @private
   * @memberof GiftCardsListComponent
   */
  private preFilterCategories(categories: any[]): any[] {
    let filteredList = categories;

    // Categories to remove from list
    if (this.filterOutCategories && this.filterOutCategories.length > 0) {
      filteredList = filteredList.filter(x => this.filterOutCategories.every(y => y != x.id));
    }

    // Categories to keep in list
    if (this.filterInCategories && this.filterInCategories.length > 0) {
      filteredList = filteredList.filter(x => this.filterInCategories.some(y => y == x.id));
    }

    return filteredList;
  }

  /**
   * Filter in and out vouchers by categoryId
   *
   * @private
   * @param {any[]} vouchers
   * @returns {any[]}
   * @memberof GiftCardsListComponent
   */
  private preFilterVouchers(vouchers: any[]): any[] {
    let filteredList = vouchers;

    // Categories to remove from list
    if (this.filterOutCategories && this.filterOutCategories.length > 0) {
      filteredList = filteredList.filter(x => x.partnerId === environment.giftSharePartnerId || this.filterOutCategories.every(y => !x.partnerCategories.some(z => z.id == y)));
    }

    // Categories to keep in list
    if (this.filterInCategories && this.filterInCategories.length > 0) {
      filteredList = filteredList.filter(x => this.filterInCategories.some(y => x.partnerCategories.some(z => z.id == y)));
    }

    return filteredList;
  }

  /**
   * Sort vouchers based on selection
   *
   * @private
   * @param {*} list
   * @param {string} sort
   * @returns
   * @memberof GiftCardsListComponent
   */
  private sortVouchers(list: any[], sort: string) {
    switch (sort) {
      case 'implicit': {
        return orderBy(list, 'rate');
      }
      case 'za': {
        return orderBy(list, '-partnerName');
      }
      default: {
        return orderBy(list, 'partnerName');
      }
    }
  }
}
